<template>
  <PopupContent :bodyClass="['referral-popup-body']">
    <template #header>
      <h2 class="title">{{ viewModel.title }}</h2>
    </template>

    <div class="option-block">
      <div class="description">{{ viewModel.webLabel }}</div>
      <div class="link-row">
        <div class="link-box">{{ viewModel.webLink }}</div>
        <div class="actions-row">
          <button class="secondary-button" type="button" @click="handleCopy(viewModel.webLink)">
            {{ viewModel.copyButtonText }}
          </button>
          <button class="primary-button" type="button" @click="handleShare(viewModel.webLink)">
            {{ viewModel.shareButtonText }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="viewModel.telegramLink" class="option-block">
      <div class="description">{{ viewModel.telegramLabel }}</div>
      <div class="link-row">
        <div class="link-box">{{ viewModel.telegramLink }}</div>
        <div class="actions-row">
          <button class="secondary-button" type="button" @click="handleCopy(viewModel.telegramLink)">
            {{ viewModel.copyButtonText }}
          </button>
          <button class="primary-button" type="button" @click="handleShare(viewModel.telegramLink)">
            {{ viewModel.shareButtonText }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="statusMessage" class="status">{{ statusMessage }}</div>

    <template #footer>
      <div class="actions">
        <div class="footer-note">{{ t('subscription.referral.footerNote') }}</div>
      </div>
      <div class="footer-close">
        <button class="secondary-button" type="button" @click="viewModel.handleClose()">
          {{ viewModel.closeButtonText }}
        </button>
      </div>
    </template>
  </PopupContent>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import PopupContent from './PopupContent.vue';
import type { ReferralInvitePopupViewModel } from '../popup-view-models/referral-invite-popup.view-model';
import { shareInviteLink } from '../../referrals/invite-links';

interface Props {
  readonly viewModel: ReferralInvitePopupViewModel;
}

const props = defineProps<Props>();
const statusMessage = ref('');
const { t } = useI18n();

async function handleCopy(inviteLink: string): Promise<void> {
  statusMessage.value = '';
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(inviteLink);
      statusMessage.value = t('subscription.referral.copied');
      return;
    }
  } catch {
    // Fall through to showing the raw link.
  }
  statusMessage.value = inviteLink;
}

async function handleShare(inviteLink: string): Promise<void> {
  statusMessage.value = '';
  try {
    const result = await shareInviteLink(inviteLink);
    statusMessage.value = result === 'copied'
      ? t('subscription.referral.copied')
      : t('subscription.referral.shared');
  } catch {
    statusMessage.value = inviteLink;
  }
}
</script>

<style scoped>
.referral-popup-body {
  color: #243447;
  text-align: center;
}

.title {
  margin: 0;
  font-size: var(--font-size-subheader);
  text-align: center;
}

.description {
  line-height: 1.5;
  color: #74879a;
  text-align: center;
}

.option-block + .option-block {
  margin-top: 50px;
}

.link-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 5px;
  padding: 8px;
  border-radius: 14px;
  background: #eef4fb;
  border: 1px solid #d9e6f3;
}

.link-box {
  padding: 12px 14px;
  border-radius: 10px;
  background: #ffffff;
  color: #183153;
  word-break: break-all;
  text-align: left;
  flex: 1 1 auto;
  min-width: 0;
}

.actions-row {
  display: flex;
  gap: 18px;
  flex-wrap: nowrap;
  justify-content: center;
  flex: 0 0 auto;
}

.status {
  margin-top: 12px;
  color: #1d7a3a;
  text-align: center;
}

.actions {
  display: flex;
  justify-content: center;
  align-items: center;
}

.footer-note {
  color: #74879a;
  text-align: center;
}

.footer-close {
  display: flex;
  justify-content: center;
  margin-top: 12px;
}

.primary-button,
.secondary-button {
  border-radius: 9px;
  padding: 7px 12px;
  font-size: 0.9rem;
  cursor: pointer;
  min-width: 96px;
  height: 40px;
}

.primary-button {
  border: none;
  background: #183153;
  color: #fff;
}

.secondary-button {
  border: 1px solid #ccd7e6;
  background: #fff;
  color: #243447;
}

</style>
